import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase, Tables } from "../utils/supabase";
import {
  ScaleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

interface Scale {
  id: string;
  brand: string;
  model: string;
  e_value: number;
  location?: string;
  status: string;
}

interface CalibrationRecord {
  id: string;
  calibration_date: string;
  status: string;
}

interface ScaleWithRecords extends Scale {
  latest_calibration?: CalibrationRecord;
  days_since_calibration: number;
  calibration_status: "current" | "overdue";
}

const CalibrationStatus: React.FC = () => {
  const { selectedPharmacy } = useAuth();
  const [scales, setScales] = useState<ScaleWithRecords[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedPharmacy) {
      fetchCalibrationStatus();
    }
  }, [selectedPharmacy]);

  const fetchCalibrationStatus = async () => {
    if (!selectedPharmacy) return;

    try {
      setLoading(true);

      // Fetch scales for the pharmacy
      const { data: scalesData, error: scalesError } = await supabase
        .from(Tables.SCALES)
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (scalesError) {
        console.error("Error fetching scales:", scalesError);
        return;
      }

      if (!scalesData) {
        setScales([]);
        return;
      }

      // Get latest calibration for each scale
      const scalesWithRecords: ScaleWithRecords[] = await Promise.all(
        scalesData.map(async (scale) => {
          const { data: latestCalibration } = await supabase
            .from(Tables.CALIBRATION_RECORDS)
            .select("*")
            .eq("scale_id", scale.id)
            .order("calibration_date", { ascending: false })
            .limit(1)
            .single();

          let calibrationStatus: "current" | "overdue" = "overdue";
          let daysSince = 0;

          if (latestCalibration) {
            const lastDate = new Date(latestCalibration.calibration_date);
            const today = new Date();
            daysSince = Math.floor(
              (today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24),
            );

            if (daysSince <= 7) {
              calibrationStatus = "current";
            } else {
              calibrationStatus = "overdue";
            }
          }

          return {
            ...scale,
            latest_calibration: latestCalibration,
            days_since_calibration: daysSince,
            calibration_status: calibrationStatus,
          };
        }),
      );

      setScales(scalesWithRecords);
    } catch (error) {
      console.error("Error fetching calibration status:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "good":
        return "text-green-600 bg-green-50";
      case "warning":
        return "text-yellow-600 bg-yellow-50";
      case "critical":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "good":
        return CheckCircleIcon;
      case "warning":
        return ClockIcon;
      case "critical":
        return ExclamationTriangleIcon;
      default:
        return ScaleIcon;
    }
  };

  const getOverallStatus = () => {
    if (scales.length === 0) return "unknown";

    const overdueScales = scales.filter(scale => scale.calibration_status === "overdue");
    const failedScales = scales.filter(scale => 
      scale.latest_calibration && scale.latest_calibration.status === "failed"
    );

    if (overdueScales.length > 0 || failedScales.length > 0) {
      return "critical";
    } else if (scales.some(scale => scale.calibration_status === "current")) {
      return "good";
    } else {
      return "warning";
    }
  };

  const getStatusText = () => {
    if (scales.length === 0) return "Geen balansen gevonden";

    const overdueScales = scales.filter(scale => scale.calibration_status === "overdue");
    const currentScales = scales.filter(scale => scale.calibration_status === "current");

    if (overdueScales.length > 0) {
      return `${overdueScales.length} balansen zijn verlopen`;
    } else if (currentScales.length === scales.length) {
      return "Alle balansen zijn gekalibreerd";
    } else {
      return `${scales.length - currentScales.length} balansen moeten nog gekalibreerd worden`;
    }
  };

  const getDaysUntilNextCheck = () => {
    const currentScales = scales.filter(scale => scale.calibration_status === "current");
    
    if (currentScales.length === 0) return null;

    const mostRecent = currentScales.reduce((latest, scale) => {
      if (!scale.latest_calibration) return latest;
      const scaleDate = new Date(scale.latest_calibration.calibration_date);
      return !latest || scaleDate > latest ? scaleDate : latest;
    }, null as Date | null);

    if (!mostRecent) return null;

    const daysSince = Math.floor((Date.now() - mostRecent.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, 7 - daysSince);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (scales.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <ScaleIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p>Geen balansen gevonden</p>
        </div>
      </div>
    );
  }

  const overallStatus = getOverallStatus();
  const StatusIcon = getStatusIcon(overallStatus);
  const daysUntilNext = getDaysUntilNextCheck();
  const overdueScales = scales.filter(scale => scale.calibration_status === "overdue");
  const currentScales = scales.filter(scale => scale.calibration_status === "current");
  const failedScales = scales.filter(scale => 
    scale.latest_calibration && scale.latest_calibration.status === "failed"
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${getStatusColor(overallStatus)}`}>
            <StatusIcon className="h-6 w-6" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Balans Kalibratie
            </h3>
            <p className="text-sm text-gray-600">
              {getStatusText()}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">
            {scales.length}
          </div>
          <div className="text-sm text-gray-500">Totaal balansen</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <div className="text-lg font-semibold text-green-600">
            {currentScales.length}
          </div>
          <div className="text-xs text-gray-500">Deze week</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-red-600">
            {overdueScales.length}
          </div>
          <div className="text-xs text-gray-500">Verlopen</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-orange-600">
            {failedScales.length}
          </div>
          <div className="text-xs text-gray-500">Gefaald</div>
        </div>
      </div>

      {daysUntilNext !== null && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center">
            <ClockIcon className="h-4 w-4 text-blue-600 mr-2" />
            <span className="text-sm text-blue-800">
              {daysUntilNext > 0 
                ? `Volgende controle over ${daysUntilNext} dag${daysUntilNext !== 1 ? 'en' : ''}`
                : "Controle nu vereist"
              }
            </span>
          </div>
        </div>
      )}

      {overdueScales.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Verlopen kalibraties:
          </h4>
          <div className="space-y-1">
            {overdueScales.slice(0, 3).map((scale) => (
              <div key={scale.id} className="text-xs text-gray-600">
                {scale.brand} {scale.model} - 
                {scale.latest_calibration 
                  ? ` ${scale.days_since_calibration} dagen geleden`
                  : " Nog nooit gekalibreerd"
                }
              </div>
            ))}
            {overdueScales.length > 3 && (
              <div className="text-xs text-gray-500">
                +{overdueScales.length - 3} meer...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalibrationStatus;
